import React, { useEffect, useState } from 'react'

import { useApplicationContext } from '../../contexts/appContext'
import { useNavigate } from 'react-router-dom'
import { BaseButton, BaseCard } from '@verifier-user/verifier-headless-ui'
import { Employer } from './Employer'
import { ChevronDoubleRightIcon } from '@heroicons/react/24/solid'
import { emailRegex, phoneRegex, SEND_TYPE } from '@verifier-user/verifier-cdr'

export const BMIntro = ({
  currentErrorRef: currentErrorRef,
  setProcessing: setProcessing,
  setError: setError,
  setErrorShown: setErrorShown
}) => {
  const smsLoginEnabled = process.env.SMS_LOGIN_ENABLED === 'true'
  const [touched, setTouched] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [showUnAuthError, setShowUnAuthError] = useState(false)

  const [sendType, setSendType] = useState(smsLoginEnabled ? null : SEND_TYPE.EMAIL)
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [employers, setEmployers] = useState([
    {
      name: '',
      statedIncome: ''
    }
  ])
  const [maritalStatus, setMaritalStatus] = useState('')

  const { login, customerLogin } = useApplicationContext()

  const navigate = useNavigate()

  useEffect(() => {
    if (customerLogin) {
      if (customerLogin.email) {
        setSendType(SEND_TYPE.EMAIL)
        setEmail(customerLogin.email)
      } else if (customerLogin.mobile) {
        setSendType(SEND_TYPE.SMS)
        setMobile(customerLogin.mobile)
      }
    }
  }, [customerLogin])

  useEffect(() => {
    setErrorShown(false)
    setShowUnAuthError(false)
    if (sendType === SEND_TYPE.EMAIL) {
      validate()
    } else {
      setIsValid(!!mobile && phoneRegex.test(mobile))
    }
  }, [sendType, email, mobile, firstName, lastName, middleName, employers])

  const validate = () => {
    const valid = Boolean(
      emailRegex.test(email || '') &&
        firstName &&
        lastName &&
        employers.length > 0 &&
        !employers.some((employer) => !employer.name || !employer.statedIncome)
    )
    setIsValid(valid)
  }

  const getValidationMessages = () => {
    const messages = []

    // Validate email
    if (!email || !emailRegex.test(email)) {
      messages.push('Please enter a valid email address.')
    }

    // Validate first name
    if (!firstName) messages.push('Please enter first name')

    // Validate last name
    if (!lastName) messages.push('Please enter last name')

    // Validate employers
    if (employers.length === 0 || employers.some((employer) => !employer.name || !employer.statedIncome)) {
      messages.push(
        'Please make sure you have added at least 1 employer and that both employerName and statedIncome are set for each employer'
      )
    }

    return (
      <ul>
        {messages.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    )
  }

  const handleChangeEmail = (event) => {
    setShowUnAuthError(false)
    setTouched(false)
    setEmail(event.target.value)
  }

  const handleChangeFirstName = (event) => {
    setShowUnAuthError(false)
    setTouched(false)
    setFirstName(event.target.value)
  }

  const handleChangeLastName = (event) => {
    setShowUnAuthError(false)
    setTouched(false)
    setLastName(event.target.value)
  }

  const handleChangeMiddleName = (event) => {
    setShowUnAuthError(false)
    setTouched(false)
    setMiddleName(event.target.value)
  }

  const validateEmail = () => {
    setTouched(true)
    validate()
  }

  const addEmployer = () => {
    setEmployers((prevEmployers) => [
      ...prevEmployers,
      {
        name: '',
        statedIncome: ''
      }
    ])
  }

  const updateEmployer = (index, key, value) => {
    setEmployers((prevEmployers) => {
      const updatedEmployers = [...prevEmployers] // Create a new array (avoid mutation)
      updatedEmployers[index] = { ...updatedEmployers[index], [key]: value } // Update the object
      return updatedEmployers
    })
  }

  const deleteEmployer = (indexToDelete) => {
    setEmployers((prevEmployers) => prevEmployers.filter((_, index) => index !== indexToDelete))
  }

  const sendCode = (event) => {
    event.preventDefault()
    setTouched(true)

    if (isValid) {
      setProcessing(true)
      const body = sendType === SEND_TYPE.EMAIL ? { email: email } : { mobile: mobile }

      body.firstName = firstName
      body.lastName = lastName
      body.middleName = middleName
      body.employers = employers

      if (maritalStatus) {
        body.maritalStatus = maritalStatus
      }

      login({
        body,
        onSuccess: () => {
          setProcessing(false)
          navigate('/code')
        },
        onError: (error) => {
          setProcessing(false)
          setError(error)
          let title
          let message
          if (error.status >= 400 && error.status < 500) {
            //BOM-417 Fix
            if (error.data === 'Email not allowed') {
              title = 'Unauthorised Email'
              message = 'Only pre-authorised email addresses are allowed to use this system.'
            } else {
              title = error.statusText
              message = error.data
            }
          } else {
            title = 'Server Unavailable'
            message =
              "We're sorry but there has been a connection issue. Please try again, and if the issue persists, start\n" +
              '                  the process again.'
          }
          currentErrorRef.current = {
            title: title,
            retryFunction: () => sendCode(event),
            cancelFunction: () => setErrorShown(false),
            okTitle: 'Try Again',
            cancelTitle: 'Cancel',
            errorContent: (
              <div>
                <p>{message}</p>
              </div>
            )
          }
          setErrorShown(true)
        }
      })
    }
  }

  return (
    <>
      <h1 data-testid='welcome-heading' className={'text-4xl sm:text-5xl mb-2 leading-tight'}>
        Verify your income
      </h1>
      <p>Welcome to Verifier — the privacy-friendly way to verify your income.</p>
      <p>
        To get started, we need your <strong>email</strong>{' '}
        {smsLoginEnabled && (
          <>
            or <strong>mobile number</strong>
          </>
        )}{' '}
        so that you will be able to access your consent dashboard.
      </p>

      <p>
        Please enter your <strong>email</strong>
        <span data-testid={'additional-info-text'}>and the following required information</span>. We'll send you a
        secure code you can use to login.
      </p>
      <form onSubmit={sendCode}>
        <BaseCard className='max-w-screen-md'>
          <label className='block cq-w-sm:w-2/3'>
            Email address
            <span className='text-red-500'>*</span>
            <input
              id='email'
              type='email'
              value={email}
              onChange={handleChangeEmail}
              onBlur={validateEmail}
              className='verifier-input'
              required
              placeholder={'Email address'}
            />
          </label>

          <label className='block cq-w-sm:w-2/3' htmlFor={'firstName'}>
            First Name
            <span className='text-red-500'>*</span>
            <input
              id='firstName'
              value={firstName}
              onChange={handleChangeFirstName}
              className='verifier-input'
              required
              placeholder={'First Name'}
            />
          </label>

          <label className='block cq-w-sm:w-2/3' htmlFor={'middleName'}>
            Middle Name
            <input
              id='middleName'
              value={middleName}
              onChange={handleChangeMiddleName}
              className='verifier-input'
              placeholder={'Middle Name'}
            />
          </label>

          <label className='block cq-w-sm:w-2/3' htmlFor={'lastName'}>
            Last Name
            <span className='text-red-500'>*</span>
            <input
              id='lastName'
              value={lastName}
              onChange={handleChangeLastName}
              className='verifier-input'
              required
              placeholder={'Last Name'}
            />
          </label>

          <label className='block cq-w-sm:w-2/3' htmlFor={'maritalStatusOptions'}>
            Marital Status
            <select
              id='maritalStatusOptions'
              data-testid={`marital-status`}
              value={maritalStatus}
              onChange={(e) => setMaritalStatus(e.target.value)}
              className='block w-1/2 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
            >
              <option disabled hidden value='' className={'text-gray-500'}>
                Select your marital status...
              </option>
              <option data-testid={`Married`} value='Married' name={`Married`}>
                Married
              </option>
              <option value='De Facto' data-testid={`de-facto`} name={`De Facto`}>
                De Facto
              </option>
              <option value='Single' data-testid={`single`} name={`De Facto`}>
                Single
              </option>
              <option value='Divorced' data-testid={`divorced`} name={`Divorced`}>
                Divorced
              </option>
              <option value='Widowed' data-testid={`widowed`} name={`Widowed`}>
                Widowed
              </option>
              <option value='Separated' data-testid={`separated`} name={`Separated`}>
                Separated
              </option>
            </select>
          </label>

          <div className={'mt-3'}>
            <strong>Primary Employer</strong>
            <Employer
              key={0}
              employer={employers[0]}
              updateEmployer={(key, value) => updateEmployer(0, key, value)}
              index={0}
            />
          </div>

          {/* Display additional employers (if any) */}
          {employers.length > 1 && (
            <>
              <div className={'mt-2'}>
                <strong>Additional Employers</strong>
              </div>
              {employers.slice(1).map((employer, index) => (
                <Employer
                  key={index + 1} // Offset index since we're slicing
                  employer={employer}
                  updateEmployer={(key, value) => updateEmployer(index + 1, key, value)}
                  index={index + 1}
                  removeEmployer={() => deleteEmployer(index + 1)}
                />
              ))}
            </>
          )}
          {employers.length > 0 && (
            <BaseButton type='button' data-testid='btn-add-employer' className='mt-2 mb-4' onClick={addEmployer}>
              Add an additional employer
              <ChevronDoubleRightIcon className='w-4 h-4 inline' />
            </BaseButton>
          )}

          {touched && !isValid && (
            <div className='verifier-error-text text-red-600 mt-2 mb-0' data-testid='login-error'>
              {getValidationMessages()}
            </div>
          )}

          {showUnAuthError && (
            <p className='verifier-error-text text-red-600 mt-2 mb-0' data-testid='un-auth-error'>
              {showUnAuthError
                ? `The ${sendType === SEND_TYPE.EMAIL ? 'email address' : 'mobile number'} does not exist`
                : "Sorry that didn't work. Please try again"}
            </p>
          )}
        </BaseCard>

        <BaseButton type='submit' data-testid='btn-send-code' className='mb-4' onClick={sendCode} id='submit-button'>
          Send me a code
          <ChevronDoubleRightIcon className='w-4 h-4 inline' />
        </BaseButton>
      </form>
    </>
  )
}
